// src/pages/LeadDetail.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

const FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL;
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const MANUAL_TYPES = ['phone_call', 'in_person', 'other'];
const STATUSES = ['New', 'Interested', 'Not Interested', 'Closed'];

function formatDateTime(v) {
  return v ? new Date(v).toLocaleString() : '—';
}

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Manual touchpoint form
  const [logOpen, setLogOpen] = useState(false);
  const [logType, setLogType] = useState('phone_call');
  const [logDirection, setLogDirection] = useState('outbound');
  const [logContent, setLogContent] = useState('');
  const [logBusy, setLogBusy] = useState(false);

  // Status override -- deliberate, not a default table control. Covers the
  // two states nothing automates (Not Interested, Closed), plus manual
  // correction if the automatic New/Interested logic ever gets it wrong.
  const [statusOverrideOpen, setStatusOverrideOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  // Cadence override -- how many days between soft-touch nudges on the
  // Follow-ups page for this specific lead. Defaults to 7 for everyone;
  // only needs touching for leads that want a tighter or looser rhythm.
  const [cadenceOpen, setCadenceOpen] = useState(false);
  const [cadenceValue, setCadenceValue] = useState('');
  const [cadenceBusy, setCadenceBusy] = useState(false);

  // Professional info + financial capacity estimate -- job_title/company/
  // linkedin_url are plain editable fields (not derived from anything);
  // capacity_score/tier/reasoning come from the estimate-capacity edge
  // function, which reasons over those fields via Claude. Not a LinkedIn
  // scraper -- just scoring whatever professional context is on the lead.
  const [capacityOpen, setCapacityOpen] = useState(false);
  const [jobTitleValue, setJobTitleValue] = useState('');
  const [companyValue, setCompanyValue] = useState('');
  const [linkedinUrlValue, setLinkedinUrlValue] = useState('');
  const [profileUrlValue, setProfileUrlValue] = useState('');
  const [contactLinkedinUrlValue, setContactLinkedinUrlValue] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [estimateBusy, setEstimateBusy] = useState(false);
  const [researchBusy, setResearchBusy] = useState(false);
  const [researchResult, setResearchResult] = useState(null);

  async function fetchAll() {
    setLoading(true);
    setError('');
    const [leadRes, interactionsRes, draftsRes] = await Promise.all([
      supabase.from('leads').select('*').eq('id', id).maybeSingle(),
      supabase.from('interactions').select('*').eq('lead_id', id).order('occurred_at', { ascending: false }),
      supabase.from('content_drafts').select('*').eq('lead_id', id).order('created_at', { ascending: false }),
    ]);
    if (leadRes.error) setError(leadRes.error.message);
    else setLead(leadRes.data);
    if (interactionsRes.error) setError(interactionsRes.error.message);
    else setInteractions(interactionsRes.data || []);
    if (draftsRes.error) setError(draftsRes.error.message);
    else setDrafts(draftsRes.data || []);
    setLoading(false);
  }

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!lead) return;
    setJobTitleValue(lead.job_title || '');
    setCompanyValue(lead.company || '');
    setLinkedinUrlValue(lead.linkedin_url || '');
    setProfileUrlValue(lead.profile_url || '');
    setContactLinkedinUrlValue(lead.contact_linkedin_url || '');
  }, [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unified timeline: real interactions (things that actually happened) plus
  // drafts still in progress (pending/approved, not yet sent -- once sent
  // they show up as an interaction instead, so we skip 'posted' drafts here
  // to avoid showing the same event twice).
  const timeline = useMemo(() => {
    const fromInteractions = interactions.map((i) => ({
      key: `interaction-${i.id}`,
      when: i.occurred_at,
      kind: 'interaction',
      platform: i.platform,
      label: i.type,
      direction: i.direction,
      content: i.content,
      outcome: i.outcome,
      score_delta: i.score_delta,
    }));
    const fromDrafts = drafts
      .filter((d) => d.status === 'pending' || d.status === 'approved' || d.status === 'rejected')
      .map((d) => ({
        key: `draft-${d.id}`,
        when: d.created_at,
        kind: 'draft',
        platform: d.platform,
        label: `${d.type} (${d.status})`,
        direction: 'outbound',
        content: d.subject ? `${d.subject}\n\n${d.draft_text}` : d.draft_text,
      }));
    return [...fromInteractions, ...fromDrafts].sort((a, b) => new Date(b.when) - new Date(a.when));
  }, [interactions, drafts]);

  async function logTouchpoint(e) {
    e.preventDefault();
    if (logBusy || !logContent.trim()) return;
    setLogBusy(true);
    const { error: rpcErr } = await supabase.rpc('record_interaction', {
      p_platform: 'manual',
      p_type: logType,
      p_lead_id: id,
      p_direction: logDirection,
      p_content: logContent.trim(),
    });
    if (rpcErr) {
      setError(rpcErr.message);
    } else {
      setLogContent('');
      await fetchAll();
    }
    setLogBusy(false);
  }

  async function setStatus(newStatus) {
    if (statusBusy || newStatus === lead.status) return;
    setStatusBusy(true);
    const { error: updateErr } = await supabase.from('leads').update({ status: newStatus }).eq('id', id);
    if (updateErr) {
      setError(updateErr.message);
    } else {
      setLead((prev) => ({ ...prev, status: newStatus }));
    }
    setStatusBusy(false);
  }

  async function setCadence(e) {
    e.preventDefault();
    const days = parseInt(cadenceValue, 10);
    if (cadenceBusy || !Number.isFinite(days) || days < 1) return;
    setCadenceBusy(true);
    const { error: updateErr } = await supabase
      .from('leads')
      .update({ followup_cadence_days: days })
      .eq('id', id);
    if (updateErr) {
      setError(updateErr.message);
    } else {
      setLead((prev) => ({ ...prev, followup_cadence_days: days }));
      setCadenceValue('');
      setCadenceOpen(false);
    }
    setCadenceBusy(false);
  }

  async function saveProfile(e) {
    e.preventDefault();
    if (profileBusy) return;
    setProfileBusy(true);
    const patch = {
      job_title: jobTitleValue.trim() || null,
      company: companyValue.trim() || null,
      linkedin_url: linkedinUrlValue.trim() || null,
      profile_url: profileUrlValue.trim() || null,
      contact_linkedin_url: contactLinkedinUrlValue.trim() || null,
    };
    const { error: updateErr } = await supabase.from('leads').update(patch).eq('id', id);
    if (updateErr) {
      setError(updateErr.message);
    } else {
      setLead((prev) => ({ ...prev, ...patch }));
    }
    setProfileBusy(false);
  }

  async function estimateCapacity() {
    if (estimateBusy) return;
    setEstimateBusy(true);
    setError('');
    try {
      const res = await fetch(`${FUNCTIONS_URL}/estimate-capacity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({ lead_id: id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Capacity estimate failed');
      setLead(json.lead);
    } catch (err) {
      setError(err.message);
    } finally {
      setEstimateBusy(false);
    }
  }

  async function runResearch() {
    if (researchBusy) return;
    setResearchBusy(true);
    setResearchResult(null);
    setError('');
    try {
      const res = await fetch(`${FUNCTIONS_URL}/research-prospect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({ lead_id: id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Research failed');
      setResearchResult(json);
      await fetchAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setResearchBusy(false);
    }
  }

  if (loading) return <div className="p-6">Loading…</div>;
  if (!lead) return <div className="p-6">Lead not found. <Link to="/leads" className="text-blue-600 underline">Back to Leads</Link></div>;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mx-auto max-w-4xl">
        <Link to="/leads" className="text-sm text-blue-600 underline">← Back to Leads</Link>

        <div className="mt-3 rounded bg-white p-6 shadow">
          <h1 className="text-2xl font-bold">{lead.name}</h1>
          <div className="mt-1 text-gray-600">
            {lead.email
              ? <a href={`mailto:${lead.email}`} className="hover:underline">{lead.email}</a>
              : '—'}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <div><div className="text-xs text-gray-500">Status</div><div className="font-medium">{lead.status || 'New'}</div></div>
            <div><div className="text-xs text-gray-500">Score</div><div className="font-medium">{lead.score ?? 0}</div></div>
            <div><div className="text-xs text-gray-500">Velocity</div><div className="font-medium">{lead.velocity || 'long'}</div></div>
            <div><div className="text-xs text-gray-500">Last Contacted</div><div className="font-medium">{formatDateTime(lead.last_contacted)}</div></div>
            <div><div className="text-xs text-gray-500">Lead Source</div><div className="font-medium">{lead.lead_source || '—'}</div></div>
            <div><div className="text-xs text-gray-500">Discovered By</div><div className="font-medium">{lead.discovered_by || '—'}</div></div>
            <div><div className="text-xs text-gray-500">Date Added</div><div className="font-medium">{formatDateTime(lead.date_added)}</div></div>
            <div><div className="text-xs text-gray-500">Updated</div><div className="font-medium">{formatDateTime(lead.updated_at)}</div></div>
            <div><div className="text-xs text-gray-500">Next Touch Due</div><div className="font-medium">{formatDateTime(lead.next_touch_due_at)}</div></div>
            <div><div className="text-xs text-gray-500">Follow-up Cadence</div><div className="font-medium">{lead.followup_cadence_days ?? 7} days</div></div>
          </div>

          {lead.comment ? (
            <div className="mt-4 rounded bg-amber-50 p-3 text-sm">
              <div className="text-xs text-gray-500 mb-1">Internal note</div>
              {lead.comment}
            </div>
          ) : null}
        </div>

        {/* Manual touchpoint logger -- for real-world contact only (a call, an
            in-person meeting). This is the deliberate override, deliberately
            not on the main Leads table. */}
        <div className="mt-4 rounded bg-white p-4 shadow">
          <button
            type="button"
            onClick={() => setLogOpen((v) => !v)}
            className="text-sm font-medium text-blue-600"
          >
            {logOpen ? '− Hide' : '+ Log an offline touchpoint (call, in-person)'}
          </button>
          {logOpen && (
            <form onSubmit={logTouchpoint} className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-4">
              <select value={logType} onChange={(e) => setLogType(e.target.value)} className="rounded border p-2">
                {MANUAL_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
              <select value={logDirection} onChange={(e) => setLogDirection(e.target.value)} className="rounded border p-2">
                <option value="outbound">I contacted them</option>
                <option value="inbound">They contacted me</option>
              </select>
              <input
                value={logContent}
                onChange={(e) => setLogContent(e.target.value)}
                placeholder="What happened"
                className="rounded border p-2 md:col-span-1"
              />
              <button type="submit" disabled={logBusy} className="rounded bg-blue-500 px-3 py-2 text-white hover:bg-blue-600 disabled:opacity-50">
                {logBusy ? 'Logging…' : 'Log it'}
              </button>
            </form>
          )}
        </div>

        {/* Status override -- deliberate, hidden by default. Not Interested
            and Closed have no automatic path; this is also the fix if the
            automatic New/Interested logic ever needs correcting. */}
        <div className="mt-4 rounded bg-white p-4 shadow">
          <button
            type="button"
            onClick={() => setStatusOverrideOpen((v) => !v)}
            className="text-sm font-medium text-blue-600"
          >
            {statusOverrideOpen ? '− Hide' : '+ Override status'}
          </button>
          {statusOverrideOpen && (
            <div className="mt-3 flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  disabled={statusBusy || s === lead.status}
                  className={`rounded px-3 py-2 text-sm ${s === lead.status ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'} disabled:cursor-not-allowed`}
                >
                  {s === lead.status ? `Current: ${s}` : `Set ${s}`}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Cadence override -- how many days between soft-touch nudges on
            the Follow-ups page. Defaults to 7 for everyone. */}
        <div className="mt-4 rounded bg-white p-4 shadow">
          <button
            type="button"
            onClick={() => setCadenceOpen((v) => !v)}
            className="text-sm font-medium text-blue-600"
          >
            {cadenceOpen ? '− Hide' : '+ Adjust follow-up cadence'}
          </button>
          {cadenceOpen && (
            <form onSubmit={setCadence} className="mt-3 flex items-center gap-2">
              <span className="text-sm text-gray-600">Nudge again after</span>
              <input
                type="number"
                min="1"
                placeholder={String(lead.followup_cadence_days ?? 7)}
                value={cadenceValue}
                onChange={(e) => setCadenceValue(e.target.value)}
                className="w-20 rounded border p-2"
              />
              <span className="text-sm text-gray-600">days</span>
              <button
                type="submit"
                disabled={cadenceBusy}
                className="rounded bg-blue-500 px-3 py-2 text-sm text-white hover:bg-blue-600 disabled:opacity-50"
              >
                {cadenceBusy ? 'Saving…' : 'Save'}
              </button>
            </form>
          )}
        </div>

        {/* Professional info + financial capacity -- editable fields plus a
            Claude-reasoned estimate over them. Not a LinkedIn scraper; just
            scoring whatever job_title/company/linkedin_url is on the lead,
            however that info got there. Collapsed by default, but the toggle
            label now says when there's already something inside -- caught
            live 2026-07-12: Bradley assumed a lead had "no information at
            all" when research had actually found her LinkedIn and website,
            just tucked behind a click with no hint anything was there. */}
        <div className="mt-4 rounded bg-white p-4 shadow">
          <button
            type="button"
            onClick={() => setCapacityOpen((v) => !v)}
            className="text-sm font-medium text-blue-600"
          >
            {capacityOpen
              ? '− Hide'
              : `+ Professional info / financial capacity${
                  lead.job_title || lead.company || lead.linkedin_url || lead.profile_url || lead.contact_linkedin_url || lead.dossier_notes ? ' (has data)' : ''
                }`}
          </button>
          {capacityOpen && (
            <div className="mt-3 flex flex-col gap-3">
              <form onSubmit={saveProfile} className="grid grid-cols-1 gap-2 md:grid-cols-4">
                <input
                  className="rounded border p-2"
                  placeholder="Job title"
                  value={jobTitleValue}
                  onChange={(e) => setJobTitleValue(e.target.value)}
                  autoComplete="off"
                />
                <input
                  className="rounded border p-2"
                  placeholder="Company"
                  value={companyValue}
                  onChange={(e) => setCompanyValue(e.target.value)}
                  autoComplete="off"
                />
                <div className="flex items-center gap-1">
                  <input
                    className="flex-1 min-w-0 rounded border p-2"
                    placeholder="LinkedIn URL"
                    value={linkedinUrlValue}
                    onChange={(e) => setLinkedinUrlValue(e.target.value)}
                    autoComplete="off"
                  />
                  {lead.linkedin_url && (
                    <a href={lead.linkedin_url} target="_blank" rel="noreferrer" className="shrink-0 text-blue-500 hover:text-blue-700" title="Open LinkedIn">↗</a>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <input
                    className="flex-1 min-w-0 rounded border p-2"
                    placeholder="Website URL"
                    value={profileUrlValue}
                    onChange={(e) => setProfileUrlValue(e.target.value)}
                    autoComplete="off"
                  />
                  {lead.profile_url && (
                    <a href={lead.profile_url} target="_blank" rel="noreferrer" className="shrink-0 text-blue-500 hover:text-blue-700" title="Open website">↗</a>
                  )}
                </div>
                <div className="flex items-center gap-1 md:col-span-4">
                  <input
                    className="flex-1 min-w-0 rounded border p-2"
                    placeholder="Contact LinkedIn (named individual at this org)"
                    value={contactLinkedinUrlValue}
                    onChange={(e) => setContactLinkedinUrlValue(e.target.value)}
                    autoComplete="off"
                  />
                  {lead.contact_linkedin_url && (
                    <a href={lead.contact_linkedin_url} target="_blank" rel="noreferrer" className="shrink-0 text-blue-500 hover:text-blue-700" title="Open contact LinkedIn">↗</a>
                  )}
                </div>
                <button type="submit" disabled={profileBusy} className="rounded bg-blue-500 px-3 py-2 text-sm text-white hover:bg-blue-600 disabled:opacity-50 md:col-span-4">
                  {profileBusy ? 'Saving…' : 'Save'}
                </button>
              </form>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={runResearch}
                  disabled={researchBusy}
                  className="rounded bg-blue-500 px-3 py-2 text-sm text-white hover:bg-blue-600 disabled:opacity-50"
                >
                  {researchBusy ? 'Researching…' : 'Run research'}
                </button>
                <button
                  type="button"
                  onClick={estimateCapacity}
                  disabled={estimateBusy || (!lead.job_title && !lead.company && !lead.linkedin_url)}
                  className="rounded bg-purple-500 px-3 py-2 text-sm text-white hover:bg-purple-600 disabled:opacity-50"
                  title={!lead.job_title && !lead.company && !lead.linkedin_url ? 'Save at least one field above first' : ''}
                >
                  {estimateBusy ? 'Estimating…' : 'Estimate financial capacity'}
                </button>
                {lead.capacity_score != null && (
                  <span className="text-xs text-gray-500">Capacity last estimated {formatDateTime(lead.capacity_estimated_at)}</span>
                )}
              </div>

              {researchResult && (
                <div className="rounded bg-blue-50 p-3 text-sm">
                  <div className="font-medium mb-1">Research complete</div>
                  <div className="text-gray-700">{researchResult.research?.notes}</div>
                  {researchResult.draft_fired && <div className="mt-1 text-green-700 text-xs">Draft queued in Content Drafts.</div>}
                  {researchResult.draft_error && <div className="mt-1 text-red-600 text-xs">Draft error: {researchResult.draft_error}</div>}
                </div>
              )}

              {lead.capacity_score != null && (
                <div className="rounded bg-purple-50 p-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-purple-200 px-2 py-1 text-xs font-medium">{lead.capacity_tier}</span>
                    <span className="font-medium">Capacity score: {lead.capacity_score}/100</span>
                  </div>
                  {lead.capacity_reasoning && <p className="mt-2 text-gray-700">{lead.capacity_reasoning}</p>}
                </div>
              )}

              {lead.dossier_notes && (
                <div className="rounded bg-gray-50 border p-3 text-sm">
                  <div className="text-xs font-medium text-gray-500 mb-1">Research notes</div>
                  <div className="whitespace-pre-wrap text-gray-700 text-xs leading-relaxed">{lead.dossier_notes}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {error ? <div className="mt-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

        {/* Full history -- every touchpoint, any channel, both directions */}
        <div className="mt-4 rounded bg-white p-4 shadow">
          <h2 className="text-lg font-semibold mb-3">History</h2>
          {timeline.length === 0 ? (
            <div className="text-sm text-gray-500">No interactions yet.</div>
          ) : (
            <div className="flex flex-col gap-3">
              {timeline.map((item) => (
                <div key={item.key} className="border-l-4 pl-3 py-1" style={{ borderColor: item.direction === 'inbound' ? '#22c55e' : '#3b82f6' }}>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span className="uppercase font-medium">{item.platform}</span>
                    <span>·</span>
                    <span>{item.label}</span>
                    <span>·</span>
                    <span>{item.direction === 'inbound' ? 'from them' : 'from Claire'}</span>
                    {item.kind === 'draft' && <><span>·</span><span className="italic">not yet sent</span></>}
                    <span className="ml-auto">{formatDateTime(item.when)}</span>
                  </div>
                  {item.content ? (
                    <div className="mt-1 whitespace-pre-wrap text-sm">{item.content}</div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
